// Firebase Auth error codes → operator-facing copy.
//
// Firebase throws FirebaseError with a `code` like "auth/invalid-credential"; it is
// NOT an RFC 7807 problem, so getErrorMessage (which reads `detail`/`title`) would
// fall through to the raw SDK string. Kept pure and separate from the login screen
// so the mapping is unit-testable without a DOM.
import { getErrorMessage } from './errors';

/** Extract a Firebase `auth/*` code from an unknown thrown value ('' when absent). */
export function authErrorCode(err: unknown): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = (err as { code: unknown }).code;
    if (typeof code === 'string' && code.startsWith('auth/')) return code;
  }
  return '';
}

export function authErrorMessage(err: unknown): string {
  switch (authErrorCode(err)) {
    case 'auth/invalid-email':
      return 'That email address is not valid.';
    case 'auth/user-disabled':
      return 'That account has been disabled.';
    // Modern Firebase collapses user-not-found and wrong-password into
    // invalid-credential (email-enumeration protection); the older codes still
    // arrive from projects with that protection off, so handle all three.
    case 'auth/invalid-credential':
    case 'auth/user-not-found':
    case 'auth/wrong-password':
      return 'Wrong email or password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a minute and try again.';
    case 'auth/network-request-failed':
      return 'Network error — check your connection and retry.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in popup. Allow popups, or use email sign-in.';
    // Serving host missing from Firebase Auth → Authorized domains. Firebase only
    // auto-authorizes a project's DEFAULT Hosting site, and this app runs on an
    // additional one — so this is a real setup step, not a user error.
    case 'auth/unauthorized-domain':
      return 'This domain is not authorised for sign-in. Add it to Firebase Auth → Authorized domains.';
    case 'auth/operation-not-allowed':
      return 'That sign-in method is disabled on this Firebase project.';
    default:
      return getErrorMessage(err);
  }
}
